import { createContext, useEffect, useReducer } from 'react';
import { firebase } from './Firebase';
import { registerDevice } from 'src/utils/registerDevice';


const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  deviceError: null,
};

const reducer = (state, action) => {
  if (action.type === 'AUTH_STATE_CHANGED') {
    const { isAuthenticated, user, deviceError } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      // Preserve any pending device error unless explicitly overwritten.
      deviceError: deviceError !== undefined ? deviceError : state.deviceError,
    };
  }

  if (action.type === 'DEVICE_REJECTED') {
    return {
      ...state,
      isAuthenticated: false,
      isInitialized: true,
      user: null,
      deviceError: action.payload.deviceError,
    };
  }

  if (action.type === 'CLEAR_DEVICE_ERROR') {
    return {
      ...state,
      deviceError: null,
    };
  }

  return state;
};

const AuthContext = createContext({
  ...initialState,
  platform: 'Firebase',
  signup: () => Promise.resolve(),
  signin: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  onceGetUsers: () => Promise.resolve(),
  CreateUser: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(
    () =>
      firebase.auth().onAuthStateChanged( async (user) => {
        if (user) {
          // Here you should extract the complete user profile to make it available in your entire app.
          // The auth state only provides basic information.

          let IDToken = await user.getIdToken(true);

          // Enforce the device policy before the app is considered authenticated.
          // This runs on both fresh logins and persisted-session resumes, and
          // gating on it here removes any race with the first protected request.
          const deviceResult = await registerDevice(IDToken);

          if (!deviceResult.ok) {
            dispatch({
              type: 'DEVICE_REJECTED',
              payload: { deviceError: deviceResult.message },
            });
            await firebase.auth().signOut();
            return;
          }

          dispatch({
            type: 'AUTH_STATE_CHANGED',
            payload: {
              isAuthenticated: true,
              user: {
                id: user.uid,
                name: user.displayName,
                avatar: user.photoURL,
                email: user.email,
                idToken: IDToken,
              },
              deviceError: null,
            },
          });
        } else {
          dispatch({
            type: 'AUTH_STATE_CHANGED',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      }),
    [dispatch],
  );

  // Sign In
  const signin = (email, password) => {
    // Clear any prior device error when a new attempt starts.
    dispatch({ type: 'CLEAR_DEVICE_ERROR' });
    return firebase.auth().signInWithEmailAndPassword(email, password);
  };

  // Sign out
  const logout = () => firebase.auth().signOut();

  const clearDeviceError = () => dispatch({ type: 'CLEAR_DEVICE_ERROR' });

  return (
    <AuthContext.Provider
      value={{
        ...state,
        platform: 'Firebase',
        signin,
        logout,
        clearDeviceError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
